// /orangtua — Portal orangtua (Prompt D)
// Daftar anak, bil belum lunas, alur bayar (transfer -> upload bukti),
// status bukti (pending/lulus/tolak), riwayat bayaran + unduh resit PDF.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Heart, Pencil, MessageCircle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { paidByInvoice, invoiceOutstanding, studentSummary, groupBy } from '../../lib/fees';
import { formatRM } from '../../lib/format';
import PageHeader from '../../components/PageHeader';
import { CardSkeleton } from '../../components/Skeletons';
import EmptyState from '../../components/EmptyState';
import StatusPill from '../../components/StatusPill';
import UserAvatar from '../../components/UserAvatar';
import StudentBillingPanel from '../../components/StudentBillingPanel';
import YearBillGrid from '../../components/YearBillGrid';
import StudentDataForm from '../../components/StudentDataForm';
import { StaggerList, StaggerItem } from '../../components/Motion';
import { waLink as buildWaLink } from '../../lib/wa';

export default function OrangtuaDashboard() {
  const { t } = useTranslation();
  const { profile, school } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [proofs, setProofs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [pendingByInvoice, setPendingByInvoice] = useState({});
  const [profilAnak, setProfilAnak] = useState(null);

  const load = async () => {
    const { data: links } = await supabase.from('parent_student_links').select('student_id, status');
    const lulus = (links || []).filter((l) => l.status === 'lulus').map((l) => l.student_id);
    setPendingCount((links || []).filter((l) => l.status === 'pending').length);
    if (lulus.length === 0) { setLoading(false); return; }
    const [{ data: students }, { data: classes }, { data: invoices }, { data: payData }, { data: tariffs }, { data: proofData }] = await Promise.all([
      supabase.from('students').select('id, nama, class_id').in('id', lulus),
      supabase.from('classes').select('id, nama'),
      supabase.from('invoices').select('id, student_id, jumlah_rm, diskon_rm, diskon_nota, jumlah_bayar_rm, status, tariff_id, due_date, periode'),
      supabase.from('payments').select('id, invoice_id, student_id, jumlah_rm, kaedah, tanggal, voided'),
      supabase.from('tariffs').select('id, nama'),
      supabase.from('payment_proofs').select('id, invoice_id, student_id, nama_file, status, alasan_tolak, dibuka_bendahara_at, created_at').in('student_id', lulus).order('created_at', { ascending: false }),
    ]);
    const payList = (payData || []).filter((p) => !p.voided);
    setPayments(payList);
    const { data: rcpts } = payList.length
      ? await supabase.from('receipts').select('id, payment_id, no_resit, drive_file_id, tanggal_cetak').in('payment_id', payList.map((p) => p.id))
      : { data: [] };
    setReceipts(rcpts || []);
    setProofs(proofData || []);
    // Invois yang terpaut bukti 'pending' melalui jadual perantara (untuk status "Dicek" di grid)
    const pendingIds = (proofData || []).filter((p) => p.status === 'pending').map((p) => p.id);
    const { data: piData } = pendingIds.length
      ? await supabase.from('proof_invoices').select('proof_id, invoice_id').in('proof_id', pendingIds)
      : { data: [] };
    const pbi = {};
    (piData || []).forEach((r) => { pbi[r.invoice_id] = true; });
    setPendingByInvoice(pbi);
    const paidMap = paidByInvoice(payList);
    const cMap = Object.fromEntries((classes || []).map((c) => [c.id, c.nama]));
    const tMap = Object.fromEntries((tariffs || []).map((x) => [x.id, x.nama]));
    const invByStudent = groupBy(invoices || [], 'student_id');
    const built = (students || []).map((s) => {
      const invs = invByStudent[s.id] || [];
      return {
        ...s, kelas: cMap[s.class_id] || '-',
        ...studentSummary(invs, paidMap),
        bills: invs.map((inv) => ({ ...inv, tarifNama: tMap[inv.tariff_id] || 'Yuran', baki: invoiceOutstanding(inv, paidMap) })),
      };
    });
    setChildren(built);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const proofsOf = (sid) => proofs.filter((p) => p.student_id === sid);
  const receiptsOf = (sid) => {
    const payIds = new Set(payments.filter((p) => p.student_id === sid).map((p) => p.id));
    return receipts.filter((r) => payIds.has(r.payment_id));
  };

  return (
    <div className="pb-24 lg:pb-0">
      <PageHeader
        title={t('anakSaya')}
        subtitle={t('selamatDatang', { nama: profile?.nama || '' })}
        breadcrumbs={[{ label: t('utama') }, { label: t('anakSaya') }]}
      />

      {pendingCount > 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber2-light p-4" data-testid="pending-banner">
          <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 dark:text-amber-200">{t('permohonanDiproses')}</p>
            <p className="text-sm text-amber-700 dark:text-amber-300">{t('permohonanMenunggu', { n: pendingCount })}</p>
          </div>
        </div>
      )}

      {loading ? <CardSkeleton count={2} /> : children.length === 0 ? (
        <EmptyState
          icon={Heart}
          title={pendingCount > 0 ? t('menungguKelulusan') : t('belumAnakDitautkan')}
          description={pendingCount > 0 ? t('kelulusanDesc') : t('tautAnakDesc')}
          testid="empty-children"
        />
      ) : (
        <>
          {children.length > 0 && (
            <div className="mb-4 rounded-2xl bg-gradient-to-r from-brand to-brand-royal p-4 sm:p-5 text-white" data-testid="family-total">
              <p className="text-xs font-medium uppercase tracking-wider text-white/70">{t('totalKeluarga')}</p>
              <p className="mt-1 text-2xl sm:text-3xl font-black font-mono tabular">
                {formatRM(children.reduce((s, c) => s + (c.outstanding || 0), 0))}
              </p>
              {(() => {
                const href = buildWaLink(school?.telefon, t('waTanyaUmum', { nama: profile?.nama || '' }));
                return href && (
                  <a href={href} target="_blank" rel="noreferrer"
                    className="mt-3 inline-flex h-10 items-center gap-2 rounded-xl bg-card/15 px-4 text-sm font-semibold text-white hover:bg-card/25">
                    <MessageCircle className="h-4 w-4" />{t('hubungiBendahara')}
                  </a>
                );
              })()}
            </div>
          )}
          <StaggerList className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {children.map((c) => (
            <StaggerItem key={c.id} className="rounded-2xl border border-border bg-card p-5 hover:-translate-y-0.5 hover:shadow-md transition-all" >
              <div className="flex items-center gap-3">
                <UserAvatar nama={c.nama} size={52} />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-foreground truncate">{c.nama}</p>
                  <p className="text-sm text-muted-foreground">{c.kelas}</p>
                </div>
                <StatusPill status={c.status} />
              </div>

              <div className="mt-4 rounded-xl bg-muted p-4">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('jumlahTunggakan')}</p>
                <p className={`mt-1 text-2xl sm:text-3xl font-black font-mono tabular ${c.outstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald2'}`} data-testid={`child-outstanding-${c.id}`}>
                  {formatRM(c.outstanding)}
                </p>
              </div>

              <button
                onClick={() => setProfilAnak(c)}
                className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted"
                data-testid={`edit-profil-${c.id}`}
              >
                <Pencil className="h-4 w-4" /> {t('kemaskiniProfil')}
              </button>

              <div className="mt-4">
                <YearBillGrid
                  student={c}
                  bills={c.bills}
                  proofs={proofsOf(c.id)}
                  pendingByInvoice={pendingByInvoice}
                  school={school}
                  onUploaded={load}
                />
              </div>

              <StudentBillingPanel
                student={c}
                bills={[]}
                proofs={proofsOf(c.id)}
                receipts={receiptsOf(c.id)}
                payments={payments}
                school={school}
                onUploaded={load}
              />
            </StaggerItem>
          ))}
          </StaggerList>

          {profilAnak && (
            <StudentDataForm
              student={profilAnak}
              open={!!profilAnak}
              onOpenChange={(o) => { if (!o) setProfilAnak(null); }}
              onSaved={() => { setProfilAnak(null); load(); }}
              rpcStatus="orangtua_student_data_status"
              rpcUpdate="orangtua_update_student_data"
              showNoKpValue
            />
          )}
        </>
      )}
    </div>
  );
}
